<form [formGroup]="formGroup" class="p-2">
    <mat-form-field appearance="outline" class="inline-20 w-100">
        <mat-label>{{ 'app.settings.user_create.step2.role_label' | translate }}</mat-label>
        <mat-icon matPrefix class="role-icon icon-sm">admin_panel_settings</mat-icon>
        <mat-select formControlName="roleId" (openedChange)="onRoleDropdownChange($event)">
            <mat-option [value]="null" class="role-search-option"
                (onSelectionChange)="$event.isUserInput && $event.source.deselect()">
                <div class="role-search-wrapper d-flex align-items-center">

                    <input class="role-search-input"
                        [placeholder]="'app.settings.user_create.step2.role_filter_placeholder' | translate"
                        (input)="onRoleSearchChange($event)" (click)="$event.stopPropagation()"
                        (mousedown)="$event.stopPropagation()" (keydown)="$event.stopPropagation()"
                        (keyup)="$event.stopPropagation()" #roleSearchInput>
                    @if (isRolesLoading()) {
                    <mat-spinner diameter="20"></mat-spinner>
                    }
                </div>
            </mat-option>
            @for (role of filteredRoles(); track role.id) {
            <mat-option [value]="role.id">
                <div class="d-flex align-items-center gap-2">
                    <span class="role-name-text">{{ role.name | titlecase }}</span>
                </div>
            </mat-option>
            }
        </mat-select>
        @if (formGroup.get('roleId')?.hasError('required')) {
        <mat-error>{{ 'app.settings.user_create.step2.role_required_error' | translate }}</mat-error>
        }
    </mat-form-field>

    <!-- PERMISSIONS SECTION -->
    <div class="permissions-body">
        @if (formGroup.get('roleId')?.value) {
        @if (facade.isPermissionsLoading()) {
        <div class="h-100 my-5 py-5">
            <av-loading-item textKey="app.settings.user_create.step2.loading_permissions"
                icon="policy"></av-loading-item>
        </div>
        } @else if (facade.selectedRolePermissions().length > 0) {
        <div class="inline-20 mt-2">
            <div class="d-flex align-items-center mb-2">
                <h4 class="m-0">{{ 'app.settings.user_create.step2.permissions_title' | translate }}</h4>
                <mat-icon [matTooltip]="'app.settings.user_create.step2.permissions_tooltip' | translate"
                    class="ms-2 info-icon icon-sm">info</mat-icon>
            </div>

            <div class="row d-flex w-100 justify-content-center">
                @for (permission of facade.selectedRolePermissions(); track permission.id; let i = $index; let last =
                $last)
                {
                <div class="d-flex col-12 p-3" [class.permission-item--scoopable]="permission.scoopable">

                    <!-- LEFT COLUMN: name, badge, instruction -->
                    <div class="d-flex align-items-top gap-2 col-4 justify-content-end pe-2 pt-3">
                        <mat-icon class=" icon-sm">verified_user</mat-icon>
                        <span class=" text-sm">{{ ('shared.models.permissions.' + permission.name + '.title') | translate
                            }}</span>
                        <mat-icon
                            [matTooltip]="(permission.scoopable
                            ? 'app.settings.user_create.step2.permission_scoped_tooltip'
                            : 'app.settings.user_create.step2.permission_global_tooltip') | translate">info</mat-icon>
                    </div>

                    <!-- RIGHT COLUMN: chips + autocomplete, or global note -->
                    <div class="col-6">
                        @if (permission.scoopable) {
                        <!-- Entity search input -->
                        <mat-form-field appearance="outline" class="scope-search-field field-compact w-100 mb-0"
                            subscriptSizing="dynamic">
                            <mat-label>{{ 'app.settings.user_create.step2.scope_search_label' | translate
                                }}</mat-label>
                            <input matInput [value]="getPermissionSearchTerm(permission.id!)"
                                (input)="onPermissionSearch(permission.id!, $event)" [matAutocomplete]="entityAuto"
                                autocomplete="off">
                            @if (permissionSearchLoading()[permission.id!]) {
                            <mat-spinner matSuffix diameter="20" class="me-3"></mat-spinner>
                            }
                            <mat-autocomplete #entityAuto="matAutocomplete"
                                (optionSelected)="onEntitySelected(permission.id!, $event)">
                                @for (entity of getPermissionSearchResults(permission.id!); track entity.uuid) {
                                <mat-option [value]="entity" class="compact-option">
                                    <div class="entity-option d-flex align-items-center justify-content-between w-100">
                                        <div class="d-flex align-items-center gap-2">
                                            <mat-icon class="entity-icon icon-sm entity-icon--{{ entity.type }}">
                                                {{ getEntityIcon(entity.type) }}
                                            </mat-icon>
                                            <div class="entity-info d-flex flex-column">
                                                <span class="entity-name text-xs">{{ entity.displayName }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </mat-option>
                                }
                            </mat-autocomplete>
                        </mat-form-field>
                        @if (getSelectedEntities(permission.id!).length > 0) {
                        <mat-chip-set class="scope-chips mb-2">
                            @for (entity of getSelectedEntities(permission.id!); track entity.uuid) {
                            <mat-chip (removed)="removeEntity(permission.id!, entity)"
                                class="custom-chip compact-chip shadow-sm">
                                <mat-icon matChipAvatar class="chip-icon icon-xs">{{ getEntityIcon(entity.type)
                                    }}</mat-icon>
                                <span class="text-xs">{{ entity.displayName }}</span>
                                <button matChipRemove
                                    [attr.aria-label]="'app.settings.user_create.step2.remove_entity' | translate">
                                    <mat-icon class="icon-xs">cancel</mat-icon>
                                </button>
                            </mat-chip>
                            }
                        </mat-chip-set>
                        }
                        } @else {
                        <p class="global-access-note ms-3 pt-3">{{
                            'app.settings.user_create.step2.global_access_note' | translate }}</p>
                        }
                    </div>

                </div>
                @if(!last) {
                <mat-divider horizontal></mat-divider>
                }
                }
            </div>
        </div>
        }
        }@else {
        <div class="d-flex flex-column align-items-center my-5 py-5">
            <mat-icon class="icon-lg">manage_accounts</mat-icon>
            <p class="mt-3 text-center">{{ 'app.settings.user_create.step2.empty_state' | translate }}</p>
        </div>
        }
    </div>

    <div class="d-flex justify-content-between align-items-center mt-4 pt-2 inline-20">
        <button mat-button matStepperPrevious>
            <mat-icon iconPositionStart>arrow_back</mat-icon>
            {{ 'app.settings.user_create.step2.back_button' | translate }}
        </button>
        <button mat-flat-button color="primary" (click)="submitRequested.emit()" [disabled]="formGroup.invalid"
            class="rounded-full shadow-1 button-compact">
            <mat-icon iconPositionStart>person_add</mat-icon>
            {{ 'app.settings.user_create.step2.submit_button' | translate }}
        </button>
    </div>
</form>